import React, { useEffect, useRef, useState } from "react";
import styled, { css } from "styled-components";
import { renderDishContents } from "../../../../utils/parsers/dishContents";
import { TaskStatuses, taskStatusMap } from "../../../../config";
import ToastService from "../../../../services/ToastService";
import OrderService from "../../../../services/OrderService";
import InfoBanner from "../../../../components/InfoBanner";
import { SecondaryText, TertiaryText } from "../../../../components/Texts";
import { formatOnlyMinutes, formatSecondsToMinutes } from "../../../../utils/date";

const Dish = styled.div<any>`
	padding: 1rem 0 0 0;
	display: flex;
	flex-direction: row;
	width: 100%;
	height: max-content;
	overflow: hidden;
	transition: 300ms all ease-in-out;
	&:not(:first-child) {
		border-top: 1px solid #a2a2a2;
	}
`;

const DishDetails = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	height: max-content;
`;

const Row = styled.div<any>`
	display: flex;
	width: 100%;
	max-height: ${(props) => props.maxHeight};
	height: ${(props) => props.height || "max-content"};
	justify-content: space-between;
	margin: ${(props) => props.margin};
	transition: 500ms max-height cubic-bezier(0, 1, 0, 1);
	-webkit-line-clamp: ${(props) => props.lineClamp};
`;

const CenteredRow = styled.div`
	display: flex;
	width: max-content;
	height: max-content;
	align-items: center;
	width: 40%;
`;

const DishTitle = styled.div`
	font-weight: 700;
	font-size: 18px;
	width: 70%;
	height: max-content;
	color: #ffffff;
	text-align: left;
	line-height: 21px;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const DishStatus = styled.div<any>`
	font-weight: 600;
	font-size: 14px;
	color: ${(props) => props.color};
	text-align: center;
	margin: 8px 0 0 auto;
	position: relative;
`;

const Line = styled.div`
	margin: 0.5rem 0 0 0;
	height: 5px;
	width: 30px;
	background: #ffcc00;
`;

const ActiveContainer = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	gap: 1rem;
	margin: 0 0 1rem 0;
`;

const ShutterComponent = styled.div``;

const Text = styled.div`
	color: #a8a8a8;
	font-weight: 500;
	font-size: 12px;
	line-height: 13px;
	text-align: left;
`;

const OpenShutterButton = styled.button<any>`
	background: #ffcc00;
	border: 1px solid #ffcc00;
	color: #000;
	border-radius: 4px;
	width: 130px;
	height: 36px;
	display: flex;
	align-items: center;
	justify-content: center;
	font-weight: 500;
	font-size: 13px;
	text-transform: uppercase;
	${(props) =>
		props.disabled &&
		css`
			pointer-events: none;
			opacity: 0.5;
		`}
`;

const Column = styled.div<any>`
	display: flex;
	flex-direction: column;
	justify-content: ${(props) => props.jContent};
	width: ${(props) => props.width || "100%"};
	height: ${(props) => props.height};
	padding: ${(props) => props.padding};
	gap: ${(props) => props.gap};
	margin: ${(props) => props.margin || "0rem 0 0 0"};
	transition: 300ms all ease-in-out;
`;

const InProgressETAText = styled(TertiaryText)`
	position: absolute;
	margin-top: 0.3rem;
	text-align: center;
	width: 100%;
`;

function handleCollectionMessage(timeLeft: number) {
	if (timeLeft <= 60) {
		//1 minute.
		return "Collect it soon to enjoy the dish fresh, but we will hold it for less than a minute.";
	} else {
		return `Collect it soon to enjoy the dish fresh, but we will hold it for ${formatSecondsToMinutes(timeLeft)}.`;
	}
}

const OrderItem = (props: any) => {
	const { orderItemData, orderId, itemIndex, syncOrderDataAfterCollectionRequest, updateDataCallLoading, uuid, errorMetaDataHandler, toggleOverlay } = props;
	const { itemId, title, status, etaText, userAllergens, allowedCollectionStartTime, hasDiscrepency, estimatedEndTime } =
		orderItemData;
	const intervalRef = useRef<any>(null);

	const isCollectionTimeValid = typeof allowedCollectionStartTime === "number";
	const isDishReady = status === TaskStatuses.READY_TO_COLLECT;
	const isDishInProgress = status === TaskStatuses.IN_PROGRESS || status === TaskStatuses.PREPARED;
	const inProgressETA = estimatedEndTime && estimatedEndTime > Date.now() ? estimatedEndTime - Date.now() : 1;

	const [timeLeft, setTimeLeft] = useState(0);
	const [buttonDisabled, setButtonDisabled] = useState(false);
	const [openRequestSent, setOpenShutterRequestSent] = useState(false);

	//Clear Interval when no is left.
	useEffect(() => {
		if (intervalRef.current && timeLeft <= 0) {
			clearInterval(intervalRef.current);
			intervalRef.current = null;
		}
	}, [timeLeft]);

	//Handle logic for starting or stopping timer when state changes.
	useEffect(() => {
		if (isDishReady && !intervalRef.current) {
			if (isCollectionTimeValid && allowedCollectionStartTime > Date.now()) {
				const remainingTime = Math.max(0, Math.floor((allowedCollectionStartTime - Date.now()) / 1000));
				setTimeLeft(remainingTime);
				intervalRef.current = setInterval(() => {
					setTimeLeft((prev) => prev - 1);
				}, 1000);
			}
		} else if (!isDishReady && intervalRef.current) {
			clearInterval(intervalRef.current);
			intervalRef.current = null;
		}
	}, [isDishReady, isCollectionTimeValid, allowedCollectionStartTime]);

	//Handle Button State.
	useEffect(() => {
		if (isDishReady) {
			if (timeLeft <= 0 || openRequestSent || updateDataCallLoading ) {
				setButtonDisabled(true);
			} else if (buttonDisabled) {
				setButtonDisabled(false);
			}
		}
	}, [timeLeft, isDishReady, buttonDisabled, updateDataCallLoading, openRequestSent]);

	useEffect(() => {
		toggleOverlay(openRequestSent);
	}, [openRequestSent])

	//Check current taskStatus
	if (!(status in taskStatusMap)) {
		console.error(`Dish Status Invalid -- ${status}`);
		return <></>;
	}

	const dishStatusConfig = taskStatusMap[status as keyof typeof taskStatusMap];
	const { statusMessage, showETA } = dishStatusConfig;

	//Call to Open Shutter
	async function openShutter() {
		setOpenShutterRequestSent(true);

		const openShutterResponse = await OrderService.openShutterV4({ itemIndex, orderId }, uuid);
		const { data, error, metadata } = openShutterResponse;
		if (data) {
			// ToastService.showToast("Shutter Open Success");
			//Call for data instantly to update the state of ther order.
			setOpenShutterRequestSent(false);
			syncOrderDataAfterCollectionRequest();
		}
		if (error || !data) {
			if (metadata && errorMetaDataHandler) {
				errorMetaDataHandler(metadata);
			} else {
				//Something went wrong in opening the shutter.
				ToastService.showToast("Shutter Open Failed");
			}
			setOpenShutterRequestSent(false);
		}
	}

	return (
		<Dish key={itemId} active={isDishReady}>
			<DishDetails>
				<Row>
					<DishTitle>{title}</DishTitle>
					<CenteredRow>
						<DishStatus id={isDishReady ? "status" : ""} color={"#D6D6D6"}>
							{showETA ? etaText : statusMessage}
							{isDishInProgress ? <InProgressETAText>ETA: {formatOnlyMinutes(inProgressETA)}</InProgressETAText> : ""}
						</DishStatus>
					</CenteredRow>
				</Row>
				<Line />
				<Column gap="0.5rem" margin="1rem 0">
					<SecondaryText>Ingredients</SecondaryText>
					<div style={{ width: "100%" }}>
						{renderDishContents({ dishData: orderItemData, ignoreDietaryPreferences: true, allergens: userAllergens })}
					</div>
				</Column>
				{isDishReady && hasDiscrepency ? (
					<InfoBanner message="Some ingredients fell less than expected. We'll be sending compensation soon please check your SMS for details!" />
				) : (
					""
				)}
				{isDishReady ? (
					<ActiveContainer id={"shutter-component"}>
						<Text>{handleCollectionMessage(timeLeft)}</Text>
						<ShutterComponent>
							<OpenShutterButton disabled={buttonDisabled} className={""} id={"shutter-open"} onClick={openShutter}>
								Collect Dish
							</OpenShutterButton>
						</ShutterComponent>
					</ActiveContainer>
				) : (
					""
				)}
			</DishDetails>
		</Dish>
	);
};

export default OrderItem;
